import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Clock, ChevronDown, ChevronUp, Download, Loader2,
  CheckCircle2, AlertCircle, Users, ArrowRight, Ticket, ShoppingCart,
} from 'lucide-react';
import LottoVerlaufGrouped from '../../components/LottoVerlaufGrouped';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

let CURRENT_ROLE = '';
try { CURRENT_ROLE = (JSON.parse(localStorage.getItem('user') || '{}').role) || ''; } catch { CURRENT_ROLE = ''; }
const IS_ADMIN = CURRENT_ROLE === 'admin' || CURRENT_ROLE === 'superadmin';

const fmtEuro = (v) => `${Number(v || 0).toFixed(2).replace('.', ',')} €`;

const formatDayTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }) + ' Uhr';
  } catch { return iso; }
};

// ── Einzelne Verlauf-Zeile (Tresor-Optik, aufklappbar) ─────────────────────
function ReportEntry({ entry, idx }) {
  const [expanded, setExpanded] = useState(false);
  const sold = entry.sold || 0;
  const isUp = entry.direction === 'up';
  return (
    <div data-testid={`lotto-history-entry-${idx}`}>
      <div className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/30">
        <button onClick={() => setExpanded((v) => !v)}
          className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex-shrink-0 transition-colors"
          data-testid={`lotto-expand-${idx}`} title="Details anzeigen">
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-gray-900 dark:text-white">{entry.product_name}</span>
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              {entry.previous_stock} <ArrowRight className="h-3 w-3" /> {entry.new_stock}
            </span>
            {entry.is_reset ? (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">neue Packung</span>
            ) : sold > 0 ? (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                {sold} verkauft · {fmtEuro(entry.revenue)}
              </span>
            ) : null}
            {isUp && sold > 0 && entry.predecessor_name && (
              <span className="text-xs text-amber-600 dark:text-amber-400">Vorgänger: {entry.predecessor_name}</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs text-gray-400">{entry.location_name || '—'}</span>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs text-gray-400">{formatDayTime(entry.reported_at)}</p>
          <p className="text-xs text-gray-500 font-medium">{entry.reported_by_name}</p>
        </div>
      </div>
      {expanded && (
        <div className="px-12 pb-3 pt-1 bg-gray-50 dark:bg-gray-800/30 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-600 dark:text-gray-300"
          data-testid={`lotto-entry-detail-${idx}`}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div><span className="text-gray-400">Getippt:</span> <b>{entry.new_stock}</b></div>
            <div><span className="text-gray-400">Vorher:</span> <b>{entry.previous_stock}</b></div>
            <div><span className="text-gray-400">Verkauf seit letzter Meldung:</span> <b>{sold}</b></div>
            <div><span className="text-gray-400">Richtung:</span> <b>{isUp ? 'vorwärts' : 'abwärts'}</b></div>
            {isUp && sold > 0 && entry.predecessor_name && (
              <div className="col-span-2 sm:col-span-4"><span className="text-gray-400">zugerechnet an Vorgänger:</span> <b className="text-amber-600 dark:text-amber-400">{entry.predecessor_name}</b></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Ansicht pro Los: ein Los = eine aufklappbare Gruppe (chronologisch) ─────
function ProLosGroup({ product, entriesAsc, idx }) {
  const [open, setOpen] = useState(idx === 0);
  const totalSold = entriesAsc.reduce((s, e) => s + (e.sold || 0), 0);
  const totalRev = entriesAsc.reduce((s, e) => s + (e.revenue || 0), 0);
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden" data-testid={`los-group-${idx}`}>
      <button onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left"
        data-testid={`los-toggle-${idx}`}>
        <div className="w-8 h-8 rounded-lg bg-[#094199]/10 flex items-center justify-center flex-shrink-0">
          <Ticket className="h-4 w-4 text-[#094199]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-gray-800 dark:text-white">{product}</p>
          <p className="text-xs text-gray-400">{entriesAsc.length} Meldungen · {totalSold} verkauft · {fmtEuro(totalRev)}</p>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>
      {open && (
        <div className="border-t border-gray-100 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
          {entriesAsc.map((e, i) => (
            <div key={e.id || i} className="flex items-center gap-3 px-5 py-2.5" data-testid={`los-row-${idx}-${i}`}>
              <span className="text-xs text-gray-400 w-32 flex-shrink-0">{formatDayTime(e.reported_at)}</span>
              <span className="text-sm font-medium text-gray-800 dark:text-gray-100 flex-1 min-w-0 truncate">{e.reported_by_name}</span>
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 flex-shrink-0">
                {e.previous_stock} <ArrowRight className="h-3 w-3" /> {e.new_stock}
              </span>
              {e.is_reset ? (
                <span className="text-xs text-gray-500 flex-shrink-0 w-40 text-right">neue Packung</span>
              ) : e.sold > 0 ? (
                <span className="text-xs text-emerald-600 font-semibold flex-shrink-0 w-40 text-right">
                  {e.sold} verk. · {fmtEuro(e.revenue)}{e.predecessor_name ? ` · ${e.predecessor_name}` : ''}
                </span>
              ) : <span className="text-xs text-gray-300 flex-shrink-0 w-40 text-right">—</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LottoMeldeVerlauf() {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({
    start_date: firstOfMonth, end_date: lastOfMonth, employee_id: '', location_id: '',
  });
  const [subTab, setSubTab] = useState('verlauf'); // 'verlauf' | 'status'
  const [history, setHistory] = useState({ entries: [], summary_per_employee: [], total_sold: 0, total_revenue: 0 });
  const [status, setStatus] = useState({ reported: [], pending: [], total_employees: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [locsRes, empRes] = await Promise.all([
          axios.get(`${API}/api/user/locations`, { headers: authHeader() }),
          axios.get(`${API}/api/kassensturz/mitarbeiter-liste`, { headers: authHeader() }),
        ]);
        setLocations(locsRes.data || []);
        setEmployees(empRes.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.employee_id) params.append('employee_id', filters.employee_id);
      if (filters.location_id) params.append('location_id', filters.location_id);

      const statusParams = new URLSearchParams();
      if (filters.start_date) statusParams.append('start_date', filters.start_date);
      if (filters.end_date) statusParams.append('end_date', filters.end_date);
      if (filters.location_id) statusParams.append('location_id', filters.location_id);

      const [histRes, statRes] = await Promise.all([
        axios.get(`${API}/api/lotto/report-history?${params.toString()}`, { headers: authHeader() }),
        axios.get(`${API}/api/lotto/report-status?${statusParams.toString()}`, { headers: authHeader() }),
      ]);
      setHistory(histRes.data || { entries: [], summary_per_employee: [], total_sold: 0, total_revenue: 0 });
      setStatus(statRes.data || { reported: [], pending: [], total_employees: 0 });
    } catch (err) {
      console.error('Lotto Melde-Verlauf load error:', err);
      toast.error('Fehler beim Laden des Melde-Verlaufs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.employee_id) params.append('employee_id', filters.employee_id);
      if (filters.location_id) params.append('location_id', filters.location_id);

      const res = await axios.get(`${API}/api/lotto/report-history/export?${params.toString()}`,
        { headers: authHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `lotto_melde_verlauf_${filters.start_date}_${filters.end_date}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('PDF erfolgreich heruntergeladen');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Fehler beim PDF-Export');
    } finally {
      setExporting(false);
    }
  };

  // Abgeleitete Gruppierungen (reine Anzeige) ───────────────────────────────
  const entriesByProduct = (() => {
    const map = {};
    (history.entries || []).forEach((e) => {
      (map[e.product_id] = map[e.product_id] || { name: e.product_name, rows: [] }).rows.push(e);
    });
    // rows kommen absteigend → für "Pro Los" aufsteigend sortieren
    return Object.entries(map).map(([pid, v]) => ({
      product_id: pid, name: v.name,
      rowsAsc: [...v.rows].reverse(),
    })).sort((a, b) => a.name.localeCompare(b.name));
  })();
  // Admin-Box: zuletzt getippter Stand je Los (entries sind bereits neueste-zuerst)
  const lastPerProduct = (() => {
    const seen = {}; const out = [];
    (history.entries || []).forEach((e) => {
      if (!seen[e.product_id]) { seen[e.product_id] = true; out.push(e); }
    });
    return out.sort((a, b) => a.product_name.localeCompare(b.product_name));
  })();
  const HISTORY_LIMIT = 15;
  const shownEntries = showAllHistory ? (history.entries || []) : (history.entries || []).slice(0, HISTORY_LIMIT);

  return (
    <div className="space-y-4" data-testid="lotto-melde-verlauf">
      {/* Filterleiste */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Von</label>
            <input type="date" value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              className="px-3 py-1.5 rounded-lg text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              data-testid="filter-start-date" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Bis</label>
            <input type="date" value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              className="px-3 py-1.5 rounded-lg text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              data-testid="filter-end-date" />
          </div>
          <button onClick={handleExport} disabled={exporting}
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#094199] text-white hover:bg-[#073279] disabled:opacity-60 transition-colors"
            data-testid="export-pdf-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            PDF Export
          </button>
        </div>

        {/* Standort-Filter (Buttons wie Tresor) */}
        {locations.length > 1 && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setFilters({ ...filters, location_id: '' })}
              className={`px-3 py-1.5 rounded-lg text-xs border font-semibold transition-all ${filters.location_id === '' ? 'bg-[#094199] text-white border-[#094199]' : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50'}`}
              data-testid="filter-loc-all">
              Alle Standorte
            </button>
            {locations.map((loc) => (
              <button key={loc.id} onClick={() => setFilters({ ...filters, location_id: loc.id })}
                className={`px-3 py-1.5 rounded-lg text-xs border font-semibold transition-all ${filters.location_id === loc.id ? 'bg-[#094199] text-white border-[#094199]' : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50'}`}
                data-testid={`filter-loc-${loc.id}`}>
                {loc.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Sub-Tabs */}
      <div className="flex gap-2">
        <button onClick={() => setSubTab('verlauf')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${subTab === 'verlauf' ? 'bg-[#094199] text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}
          data-testid="subtab-verlauf">
          Melde-Verlauf
        </button>
        <button onClick={() => setSubTab('status')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${subTab === 'status' ? 'bg-[#094199] text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}
          data-testid="subtab-status">
          Gemeldet / Ausstehend
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-[#094199]" />
        </div>
      ) : subTab === 'verlauf' ? (
        <LottoVerlaufGrouped
          startDate={filters.start_date}
          endDate={filters.end_date}
          locationId={filters.location_id}
          showSummary={true}
        />
      ) : (
        /* Gemeldet / Ausstehend */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="report-status">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Hat gemeldet ({status.reported.length})</span>
            </div>
            {status.reported.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-500 text-center">Niemand hat im Zeitraum gemeldet.</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {status.reported.map((r) => (
                  <div key={r.employee_id} className="flex items-center justify-between px-5 py-3" data-testid={`reported-${r.employee_id}`}>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{r.name}</span>
                    <div className="text-right">
                      <p className="text-xs text-emerald-600 font-semibold">{r.count} Meldungen</p>
                      <p className="text-xs text-gray-400">zuletzt {formatDayTime(r.last_reported_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Ausstehend ({status.pending.length})</span>
            </div>
            {status.pending.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-500 text-center">Alle haben gemeldet. 🎉</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {status.pending.map((p) => (
                  <div key={p.employee_id} className="flex items-center justify-between px-5 py-3" data-testid={`pending-${p.employee_id}`}>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{p.name}</span>
                    <span className="text-xs text-amber-600 font-medium">noch keine Meldung</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
